import React from 'react';

interface CompletionStepProps {
  projectDetails: any;
  selectedPackage: string;
  addons: any;
  timeline: string;
}

const CompletionStep: React.FC<CompletionStepProps> = ({
  projectDetails,
  selectedPackage,
  addons,
  timeline,
}) => {
  const calculateTotal = () => {
    const basePrice = {
      webapp: 795,
      app: 995,
      both: 1250,
    };

    const addonPrices: { [key: string]: number } = {
      inlogRegistratie: 199,
      androidIOS: 359,
      extraIntegraties: 199,
      seoOptimalisatie: 139,
      googleAds: 139,
      appStorePublicatie: 139,
      betaalIntegratie: 299,
      pushNotificaties: 139,
      pages5to10: 249,
      pages10plus: 449,
      adminDashboard: 249,
      emailFlows: 179,
    };

    let total = basePrice[selectedPackage as keyof typeof basePrice];

    Object.keys(addons).forEach((key) => {
      if (addons[key as keyof typeof addons]) {
        if (addonPrices[key]) {
          total += addonPrices[key];
        }
      }
    });

    if (timeline === 'superfast') {
      total *= 1.5;
    } else if (timeline === '2weeks') {
      total *= 1.25;
    }

    return total;
  };

  return (
    <div className="container mx-auto px-8 py-16">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 shadow-2xl text-center">
          <div className="mb-8">
            <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse-slow"
              style={{
                background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
              }}>
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h2 className="text-4xl font-bold text-blue-800 mb-4">
              Bedankt voor je aanvraag!
            </h2>

            <p className="text-lg text-gray-600">
              We hebben je aanvraag ontvangen en nemen binnen 24 uur contact met je op.
            </p>
          </div>

          <div className="bg-blue-50 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-bold text-blue-800 mb-4">Samenvatting van je aanvraag:</h3>

            <div className="space-y-3 text-left">
              {projectDetails.projectName && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Project:</span>
                  <span className="font-semibold text-gray-800">{projectDetails.projectName}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-gray-600">Pakket:</span>
                <span className="font-semibold text-gray-800">
                  {selectedPackage === 'webapp' && 'Webapp'}
                  {selectedPackage === 'app' && 'App'}
                  {selectedPackage === 'both' && 'Website + App'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Levertijd:</span>
                <span className="font-semibold text-gray-800">
                  {timeline === '2weeks' && '2 Weken'}
                  {timeline === '1month' && '1 Maand'}
                  {timeline === 'superfast' && 'Super Snel (1 Week)'}
                </span>
              </div>

              <div className="border-t pt-3 flex justify-between">
                <span className="text-gray-600 font-semibold">Geschatte prijs:</span>
                <span className="font-bold text-2xl text-blue-600">
                  € {calculateTotal().toLocaleString('nl-NL')},-
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-800">Wat gebeurt er nu?</h3>

            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="flex items-start">
                <div className="bg-orange-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Contact opnemen</h4>
                  <p className="text-sm text-gray-600">We nemen binnen 24 uur contact met je op</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-orange-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Bespreken & starten</h4>
                  <p className="text-sm text-gray-600">Na akkoord starten we direct met je project</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-orange-500 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Oplevering</h4>
                  <p className="text-sm text-gray-600">Je project wordt op tijd opgeleverd</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t">
            <p className="text-gray-600 mb-4">Heb je vragen? Neem gerust contact op:</p>
            <div className="flex justify-center space-x-8">
              <a href="mailto:info@snaplogic.nl" className="text-blue-600 hover:text-blue-700 font-semibold">
                info@snaplogic.nl
              </a>
              <a href="tel:+31610654827" className="text-blue-600 hover:text-blue-700 font-semibold">
                06-10654827
              </a>
            </div>
          </div>

          <div className="mt-8">
            <a
              href="/"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition"
            >
              Terug naar Home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletionStep;